// eyeq — cabinet contracts: UI languages (i18n-in-DB), member bootstrap,
// history, team view. CABINETS_SPEC §2–4; auth model per ADR-0042 (route
// classes public|participant|lead|manager|machine, roles from eyeq_members).
import { z } from 'zod';

// 26 collection locales → 21 participant UI languages + ru (manager,
// internal) = 22 packs. en is the canon + compiled-in front-end fallback;
// pt splits into pt-PT/pt-BR from day one; ar is one MSA pack (RTL).
// The DB seed (migration 0013) must cover EVERY key in EVERY language —
// completeness is pinned by apps/eyeq-api/test/i18n_completeness.test.ts.
export const EYEQ_UI_LANGS = [
  'en', 'ro', 'el', 'cs', 'hr', 'pl', 'tr', 'ja', 'ko',
  'zh-Hans', 'zh-Hant', 'pt-PT', 'pt-BR', 'it', 'ar', 'es',
  'sw', 'vi', 'th', 'hi', 'fil', 'ru',
] as const;
export const EyeqUiLangSchema = z.enum(EYEQ_UI_LANGS);
export type EyeqUiLang = z.infer<typeof EyeqUiLangSchema>;

/** UI languages written right-to-left (front-end sets document dir). */
export const EYEQ_RTL_LANGS: readonly EyeqUiLang[] = ['ar'];

// 'link_qa' (ADR-0052) is a dedicated link annotator: participant-level access
// with its own task pool, NOT a step on the participant → lead → manager
// ladder. Anything that ranks roles must map it to participant rank explicitly
// — a role missing from the rank table would compare as undefined and sail
// through every gate.
export const EYEQ_MEMBER_ROLES = ['participant', 'link_qa', 'qa', 'lead', 'manager'] as const;
export const EyeqMemberRoleSchema = z.enum(EYEQ_MEMBER_ROLES);
export type EyeqMemberRole = z.infer<typeof EyeqMemberRoleSchema>;

// ── i18n pack ───────────────────────────────────────────────────────────────
export const EyeqI18nQuerySchema = z.object({
  lang: EyeqUiLangSchema,
  ns: z.string().trim().min(1).max(64).default('cab'),
});
export type EyeqI18nQuery = z.infer<typeof EyeqI18nQuerySchema>;

export const EyeqI18nPackResSchema = z.object({
  lang: EyeqUiLangSchema,
  ns: z.string(),
  keys: z.record(z.string(), z.string()),
});
export type EyeqI18nPackRes = z.infer<typeof EyeqI18nPackResSchema>;

// ── cabinet bootstrap (/cabinet/me) ─────────────────────────────────────────
export const EyeqCabinetMeResSchema = z.object({
  ok: z.literal(true),
  member: z.object({
    public_id: z.string(),
    role: EyeqMemberRoleSchema,
    country: z.string(),
    locales: z.array(z.string()),
    ui_lang: z.string(),
    status: z.string(),
    depth: z.number().int(),
    icf_signed: z.boolean(),
  }),
  balance: z.object({
    total_cents: z.number().int(),
    week_cents: z.number().int(),
  }),
  assets: z.object({
    accepted: z.number().int(),
    in_review: z.number().int(),
    rejected: z.number().int(),
  }),
});
export type EyeqCabinetMeRes = z.infer<typeof EyeqCabinetMeResSchema>;

// ── history (/cabinet/history) ──────────────────────────────────────────────
export const EyeqCabinetHistoryResSchema = z.object({
  ok: z.literal(true),
  items: z.array(
    z.object({
      asset_id: z.string(),
      status: z.string(),
      cat_id: z.string(),
      locale: z.string(),
      created_at: z.string(),
    }),
  ),
});
export type EyeqCabinetHistoryRes = z.infer<typeof EyeqCabinetHistoryResSchema>;
